import { useEffect, useState, useCallback } from 'react'
import { Modal, ActionIcon, Group, Text, Box, UnstyledButton, Image } from '@mantine/core'
import { IconX, IconChevronLeft, IconChevronRight, IconExternalLink } from '@tabler/icons-react'
import type { PhotoForDisplay } from '../../lib/types'
import styles from './PhotoLightbox.module.css'

interface PhotoLightboxProps {
  opened: boolean
  onClose: () => void
  photos: PhotoForDisplay[]
  initialIndex?: number
  showGuestName?: boolean
}

/**
 * Custom photo lightbox component
 * Displays full-size photos in a modal with navigation arrows
 */
export function PhotoLightbox({
  opened,
  onClose,
  photos,
  initialIndex = 0,
  showGuestName = false,
}: PhotoLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Update current index when initialIndex changes
  useEffect(() => {
    if (opened && initialIndex >= 0 && initialIndex < photos.length) {
      setCurrentIndex(initialIndex)
    }
  }, [opened, initialIndex, photos.length])

  const handleNext = useCallback(() => {
    if (isTransitioning || photos.length <= 1) return
    setIsTransitioning(true)
    setCurrentIndex((prev) => (prev + 1) % photos.length)
    setTimeout(() => setIsTransitioning(false), 300)
  }, [isTransitioning, photos.length])

  const handlePrevious = useCallback(() => {
    if (isTransitioning || photos.length <= 1) return
    setIsTransitioning(true)
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length)
    setTimeout(() => setIsTransitioning(false), 300)
  }, [isTransitioning, photos.length])

  const handleIndicatorClick = useCallback(
    (index: number) => {
      if (isTransitioning || index === currentIndex) return
      setIsTransitioning(true)
      setCurrentIndex(index)
      setTimeout(() => setIsTransitioning(false), 300)
    },
    [isTransitioning, currentIndex]
  )

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!opened) return

      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft') {
        handlePrevious()
      } else if (e.key === 'ArrowRight') {
        handleNext()
      }
    }

    if (opened) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [opened, onClose, handleNext, handlePrevious])

  if (photos.length === 0 || !opened) {
    return null
  }

  const currentPhoto = photos[currentIndex]
  if (!currentPhoto) {
    return null
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="100%"
      padding={0}
      radius={0}
      className={styles.modal}
      styles={{
        body: {
          padding: 0,
          height: '100vh',
        },
        content: {
          height: '100vh',
          maxHeight: '100vh',
        },
      }}
      closeOnClickOutside
      withCloseButton={false}
      trapFocus
      aria-labelledby="lightbox-title"
    >
      <Box className={styles.lightboxContainer}>
        {/* Close Button */}
        <Box className={styles.closeButtonWrapper}>
          <ActionIcon
            size="xl"
            variant="filled"
            color="dark"
            onClick={onClose}
            aria-label="Schließen"
          >
            <IconX size={24} />
          </ActionIcon>
        </Box>

        {/* Navigation Arrows */}
        {photos.length > 1 && (
          <>
            <UnstyledButton
              className={styles.navButton}
              style={{ left: '1rem' }}
              onClick={handlePrevious}
              aria-label="Vorheriges Foto"
            >
              <ActionIcon size="xl" variant="filled" color="dark">
                <IconChevronLeft size={24} />
              </ActionIcon>
            </UnstyledButton>

            <UnstyledButton
              className={styles.navButton}
              style={{ right: '1rem' }}
              onClick={handleNext}
              aria-label="Nächstes Foto"
            >
              <ActionIcon size="xl" variant="filled" color="dark">
                <IconChevronRight size={24} />
              </ActionIcon>
            </UnstyledButton>
          </>
        )}

        {/* Image Container */}
        <Box className={styles.slideContainer}>
          <Box className={styles.imageContainer}>
            <Image
              src={currentPhoto.fullSizeUrl}
              alt={currentPhoto.filename}
              className={styles.fullImage}
              loading="eager"
              mah="100%"
              h="auto"
              fit="contain"
            />
          </Box>

          {/* Photo Info */}
          <Box className={styles.photoInfo}>
            <Group justify="space-between" align="center" wrap="wrap">
              <Box>
                {showGuestName && currentPhoto.guestName && (
                  <Text size="sm" fw={600} c="white">
                    {currentPhoto.guestName}
                  </Text>
                )}
                <Text size="xs" c="dimmed">
                  {currentPhoto.filename}
                </Text>
              </Box>
              <Group align="center" gap="sm">
                <Text size="sm" c="white" fw={500}>
                  {currentIndex + 1} von {photos.length}
                </Text>
                <ActionIcon
                  size="lg"
                  variant="light"
                  color="gray"
                  component="a"
                  href={currentPhoto.fullSizeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Original öffnen"
                  title="Original öffnen"
                >
                  <IconExternalLink size={18} />
                </ActionIcon>
              </Group>
            </Group>
          </Box>
        </Box>

        {/* Indicators */}
        {photos.length > 1 && (
          <Box className={styles.indicators}>
            {photos.map((_, index) => (
              <UnstyledButton
                key={index}
                className={`${styles.indicator} ${index === currentIndex ? styles.indicatorActive : ''}`}
                onClick={() => handleIndicatorClick(index)}
                aria-label={`Gehe zu Foto ${index + 1}`}
              />
            ))}
          </Box>
        )}
      </Box>
    </Modal>
  )
}
